/**
 * PDP media gallery: toggle zoom via magnifier control (plus Escape).
 * On hover-capable devices, leaving the full gallery area closes and resets zoom.
 * Zoom view is clipped to the gallery slide via overflow:hidden on the container.
 * A single zoom toggle button lives outside the swiper slides and targets the active slide.
 */

let pdpGalleryEscapeKeyHandler = null;

export function initMediaGalleryZoom(container, options = {}) {
    const { zoomLevel = 2.5, swiper } = options;
    const zoomContainers = container.querySelectorAll('.pdp-media-gallery__zoom-container');
    if (!zoomContainers.length) return;

    let zoomToggle = container.querySelector('.pdp-media-gallery__zoom-toggle');
    if (zoomToggle && zoomToggle.parentNode) {
        const replacement = zoomToggle.cloneNode(true);
        zoomToggle.replaceWith(replacement);
        zoomToggle = container.querySelector('.pdp-media-gallery__zoom-toggle');
    }
    const canHover = () => window.matchMedia('(hover: hover)').matches;

    const setMainSwiperTouch = (enabled) => {
        if (swiper) swiper.allowTouchMove = enabled;
    };

    const activeContainer = () =>
        [...zoomContainers].find((c) => c.classList.contains('pdp-media-gallery__zoom-container--active'));

    const deactivate = (zoomContainer) => {
        zoomContainer.classList.remove('pdp-media-gallery__zoom-container--active');
        if (!activeContainer()) {
            if (zoomToggle) zoomToggle.setAttribute('aria-expanded', 'false');
            setMainSwiperTouch(true);
        }
    };

    const deactivateAll = () => {
        zoomContainers.forEach((c) => c.classList.remove('pdp-media-gallery__zoom-container--active'));
        if (zoomToggle) zoomToggle.setAttribute('aria-expanded', 'false');
        setMainSwiperTouch(true);
    };

    const getActiveSlideContainer = () => {
        const activeSlide = container.querySelector('.swiper-slide-active');
        return activeSlide?.querySelector('.pdp-media-gallery__zoom-container') ?? null;
    };

    if (swiper) {
        swiper.on('slideChange', deactivateAll);
    }

    if (pdpGalleryEscapeKeyHandler) {
        document.removeEventListener('keydown', pdpGalleryEscapeKeyHandler);
    }
    pdpGalleryEscapeKeyHandler = (e) => {
        if (e.key !== 'Escape') return;
        if (activeContainer()) deactivateAll();
    };
    
    document.addEventListener('keydown', pdpGalleryEscapeKeyHandler);

    // Store per-container activate functions so the single toggle can call the right one
    const activateMap = new Map();

    zoomContainers.forEach((zoomContainer) => {
        const img = zoomContainer.querySelector('img');
        const zoomResult = zoomContainer.querySelector('.pdp-media-gallery__zoom-result');
        if (!img || !zoomResult) return;

        let bgX = 0;
        let bgY = 0;
        let touchStartX = 0;
        let touchStartY = 0;
        let dragging = false;

        const dims = () => {
            const r = zoomContainer.getBoundingClientRect();
            return { w: r.width, h: r.height };
        };

        const applyBackgroundPosition = () => {
            zoomResult.style.backgroundPosition = `-${bgX}px -${bgY}px`;
        };

        const clampPan = () => {
            const { w, h } = dims();
            const maxX = Math.max(0, w * zoomLevel - w);
            const maxY = Math.max(0, h * zoomLevel - h);
            bgX = Math.min(maxX, Math.max(0, bgX));
            bgY = Math.min(maxY, Math.max(0, bgY));
        };

        const syncBackgroundFromImage = () => {
            const { w, h } = dims();
            if (!w || !h) return;
            const zoomSrc = img.dataset.zoomSrc || img.src;
            zoomResult.style.backgroundImage = `url('${zoomSrc}')`;
            const bw = img.offsetWidth * zoomLevel;
            const bh = img.offsetHeight * zoomLevel;
            zoomResult.style.backgroundSize = `${bw}px ${bh}px`;
        };

        const centerPan = () => {
            const { w, h } = dims();
            bgX = Math.max(0, (w * zoomLevel - w) / 2);
            bgY = Math.max(0, (h * zoomLevel - h) / 2);
            clampPan();
            applyBackgroundPosition();
        };

        const panFromClient = (clientX, clientY) => {
            const rect = zoomContainer.getBoundingClientRect();
            const x = clientX - rect.left;
            const y = clientY - rect.top;
            bgX = x * zoomLevel - rect.width / 2;
            bgY = y * zoomLevel - rect.height / 2;
            clampPan();
            applyBackgroundPosition();
        };

        const panByDelta = (deltaX, deltaY) => {
            bgX -= deltaX;
            bgY -= deltaY;
            clampPan();
            applyBackgroundPosition();
        };

        const activate = () => {
            syncBackgroundFromImage();
            zoomContainer.classList.add('pdp-media-gallery__zoom-container--active');
            if (zoomToggle) zoomToggle.setAttribute('aria-expanded', 'true');
            centerPan();
            setMainSwiperTouch(false);
        };

        activateMap.set(zoomContainer, activate);

        if (canHover()) {
            zoomContainer.addEventListener('mouseleave', () => {
                deactivate(zoomContainer);
            });
        }

        zoomResult.addEventListener('mousemove', (e) => {
            if (!zoomContainer.classList.contains('pdp-media-gallery__zoom-container--active')) return;
            if (dragging) return;
            panFromClient(e.clientX, e.clientY);
        });

        zoomResult.addEventListener(
            'touchstart',
            (e) => {
                if (!zoomContainer.classList.contains('pdp-media-gallery__zoom-container--active')) return;
                dragging = true;
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                e.preventDefault();
            },
            { passive: false }
        );

        zoomResult.addEventListener(
            'touchmove',
            (e) => {
                if (!zoomContainer.classList.contains('pdp-media-gallery__zoom-container--active') || !dragging) return;
                const touchX = e.touches[0].clientX;
                const touchY = e.touches[0].clientY;
                panByDelta(touchX - touchStartX, touchY - touchStartY);
                touchStartX = touchX;
                touchStartY = touchY;
                e.preventDefault();
            },
            { passive: false }
        );

        zoomResult.addEventListener('touchend', () => {
            dragging = false;
        });

        img.addEventListener('load', () => {
            if (zoomContainer.classList.contains('pdp-media-gallery__zoom-container--active')) {
                syncBackgroundFromImage();
                centerPan();
            }
        });

        const ro = new ResizeObserver(() => {
            if (zoomContainer.classList.contains('pdp-media-gallery__zoom-container--active')) {
                syncBackgroundFromImage();
                clampPan();
                applyBackgroundPosition();
            }
        });
        ro.observe(zoomContainer);
    });

    if (zoomToggle) {
        zoomToggle.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (activeContainer()) {
                deactivateAll();
            } else {
                const activeSlideContainer = getActiveSlideContainer();
                if (activeSlideContainer) {
                    const activate = activateMap.get(activeSlideContainer);
                    if (activate) activate();
                }
            }
        });
    }
}
